import {
  authFormClass,
  authPageContainerClass,
} from "@/layout/auth-layout.styles";
import {
  ArrowBackIcon as ArrowBack,
  Button,
  Typography,
} from "@iluvatar/ui-kit";
// libraries
import * as Yup from "yup";

import { PublicAuthLayoutContext } from "@/contexts/public-auth.context";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useContext, useState } from "react";

// components
import { EmailField } from "../components/Form/Fields/Email";

// services
import requestService from "../services/request.service";

import { useToastContext } from "../contexts/toast.context";
import { useToggle } from "../hooks/useToggle";

// constants
import { FORM_GLOBAL_ERROR_KEY } from "@iluvatar/global/src/constants";

// styles

// types
import { FormHelpers, RootFormProvider } from "../FormLib";

type PasswordResetFormValue = {
  email: string;
  // for displaying general form errors
  authForm?: never;
};

const AUTH_FORM_VALIDATOR = Yup.object().shape({
  email: Yup.string().required().email(),
});

const PasswordResetPage: React.FC = () => {
  const layout = useContext(PublicAuthLayoutContext);
  const { addToast } = useToastContext();
  const [showInstructions, setShowInstructions] = useToggle(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authFormSubmission = async (
    value: PasswordResetFormValue,
    helpers: FormHelpers<PasswordResetFormValue>,
  ) => {
    setIsSubmitting(true);
    try {
      await requestService.request("/api/password-reset", {
        method: "post",
        body: JSON.stringify(value),
      });
      addToast("Password reset email sent");
      setShowInstructions(true)();
    } catch (error) {
      // oxlint-disable-next-line
      const err = error as any;
      if (err.message && err.message === "Email address not found") {
        helpers.setFieldError(
          "email",
          "We can not find an account that matches that email address. Please contact your system administrator for further assistance.",
        );
      } else if (err?.message) {
        helpers.setFieldError(FORM_GLOBAL_ERROR_KEY, err.message);
      } else {
        helpers.setFieldError(FORM_GLOBAL_ERROR_KEY, err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${authPageContainerClass} mx-auto`}>
      {!showInstructions && (
        <>
          <Typography
            variant="h5"
            className={[
              "auth-title",
              "[color:rgb(var(--uik-subtle))] [margin-top:24px]",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            Reset Password
          </Typography>
          <RootFormProvider<PasswordResetFormValue>
            id="password-reset-form"
            initialValues={{
              email: layout?.passwordResetEmail ?? "",
              authForm: undefined,
            }}
            onSubmit={authFormSubmission}
            validationSchema={AUTH_FORM_VALIDATOR}
            disabledEnterNextFieldNavigation
            className={`${authFormClass}`}
          >
            <EmailField
              required
              name="email"
              label="Email"
              className="auth-form-element"
            />
            <Button
              variant="outline"
              type="submit"
              disabled={isSubmitting}
              className={[
                "auth-form-element auth-button bg-[var(--color-background-paper)]",
                "w-full",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              Send Instructions
            </Button>
          </RootFormProvider>
        </>
      )}
      {showInstructions && (
        <>
          <Typography
            variant="h5"
            className={[
              "auth-title",
              "[color:rgb(var(--uik-subtle))] [margin-top:24px] [margin-bottom:24px]",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            Instructions Sent!
          </Typography>
          <Typography
            variant="body1"
            className="[margin-bottom:16px] [max-width:400px]"
          >
            We found an account that matched your email address and have sent
            you instructions on how to reset your password.
          </Typography>
          <Typography
            variant="body1"
            className="[margin-bottom:40px] [max-width:400px]"
          >
            If you can&apos;t find the email check your spam filter or search
            your inbox
          </Typography>
        </>
      )}
      <Link
        to="/login"
        className="auth-secondary-link flex f-c-c items-center justify-center"
        style={{ color: "var(--color-text--secondary)" }}
      >
        <ArrowBack className="[margin-right:8px] [font-size:16px]" />
        Back to login
      </Link>
    </div>
  );
};

export const Route = createFileRoute("/password-reset")({
  component: PasswordResetPage,
});
